/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import {Row, Col} from "react-bootstrap";

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../resources/validations/validations";
import { useUserContext } from "../../../contexts/userContext";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { PAGE_TITLES, AIRLINES } from "../../../constants/appConstants";

/* Components */
import ProfileSkeleton from "../../skeletonStructureProfile";

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'traveler.details.firstNamePlaceholder'
  },
  lastNamePlaceholder: {
    id: 'traveler.details.lastNamePlaceholder'
  },
  emailPlaceholder: {
    id: 'traveler.details.emailPlaceholder'
  },
  employeeIdPlaceholder: {
    id: 'traveler.details.employeeIdPlaceholder.UC'
  },
})

const ProfileTabUC = ({travelerDetails}) => {
  const theme = useTheme();
  const classes = styles({theme});
  const intl = useIntl();

  const formRef = useRef(null);

  const { travelerDetailsMockedApiValidationObj } = useValidationService()
  const [travelerSchema, setTravelerSchema] = useState({});

  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    (async () => {
      const initUtagData = {
        channel: 'content',
        pageName: PAGE_TITLES["profile"]
      };
      initAnalytics(initUtagData, 'init call', 'view')

      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(travelerDetailsMockedApiValidationObj);
      setTravelerSchema(result)
    })();
  }, []);

  return (
    <div className={classes.mainFormDiv}>
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="traveler.details.title.UC"/>
          </div>
          <div className={classes.description}>
            <FormattedMessage id="traveler.details.subtitle"/>
          </div>
        </div>
      </div>
      <div className={classes.dividerContainer}>
        <hr className={classes.border}/>
      </div>
      {Object.keys(travelerDetails).length === 0 ? (<ProfileSkeleton />) : (
        <>
          {
            <Formik
              innerRef={formRef}
              initialValues={{
                firstName: travelerDetails?.firstName,
                lastName: travelerDetails?.lastName,
                emailAddress: travelerDetails?.emailAddress,
                employeeId: travelerDetails?.employeeId
              }}
              validationSchema={travelerSchema}
            >
              {({
                  values,
                  errors,
                  touched,
                  setFieldTouched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue
                }) => {
                return (
                  <>
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-12 col-xl-4">
                          <div className={classes.personalDetails}>
                            <FormattedMessage id="traveler.details.personal.UC"/>
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="traveler.details.descr.UC"/>
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <Row>
                              <Col xs={12} xl={6}>
                                <div className={classes.inputContainer}>
                                  <label htmlFor="status" className={classes.label}>
                                    <FormattedMessage id="traveler.details.firstName"/>
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                    type="text"
                                    name="firstName"
                                    id="firstName"
                                    className={
                                      errors.firstName && touched.firstName
                                        ? "input-error"
                                        : null
                                    }
                                    disabled={true}
                                  />
                                  <ErrorMessage
                                    name="firstName"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </Col>
                              <Col xs={12} xl={6}>
                                <div className={classes.inputContainer}>
                                  <label htmlFor="status" className={classes.label}>
                                    <FormattedMessage id="traveler.details.lastName"/>
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                    type="text"
                                    name="lastName"
                                    id="lastName"
                                    className={
                                      errors.lastName && touched.lastName
                                        ? "input-error"
                                        : null
                                    }
                                    disabled={true}
                                  />
                                  <ErrorMessage
                                    name="lastName"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <div className={classes.inputContainer}>
                                <label htmlFor="status" className={classes.label}>
                                  <FormattedMessage id="traveler.details.emailAddress.UC"/>
                                </label>
                                <Field
                                  placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                  type="text"
                                  name="emailAddress"
                                  id="emailAddress"
                                  className={
                                    errors.emailAddress && touched.emailAddress
                                      ? "input-error"
                                      : null
                                  }
                                  disabled={true}
                                />
                                <ErrorMessage
                                  name="emailAddress"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            </Row>
                            {travelerDetails?.employeeId &&
                              <Row>
                                <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                                  <label htmlFor="status" className={classes.label}>
                                    <FormattedMessage id="traveler.details.employeeId.UC"/>
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.employeeIdPlaceholder)}
                                    type="text"
                                    name="employeeId"
                                    id="employeeId"
                                    className={
                                      errors.employeeId && touched.employeeId
                                        ? "input-error"
                                        : null
                                    }
                                    disabled={true}
                                  />
                                  <ErrorMessage
                                    name="employeeId"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </Row>
                            }
                          </div>
                          <div className={classes.sourceDetails}><FormattedMessage id="traveler.details.sourceDetails"/></div>
                        </div>
                      </div>
                    </form>
                  </>
                )
              }
              }
            </Formik>
          }
        </>
      )}
    </div>
  )
}

export default ProfileTabUC;